import {ADD_NODE_WITH_CUSTOM_VALUE_TEMPLATE, CUSTOM_VALUE_TYPES} from "../../Constants";

/**
 * Extension point isolating the "custom value" template variant behavior out of the
 * AWSAppConfiguration create-config component (UTR Architecture Tenet 1: isolate variant behavior
 * behind extension points; Tenet 9: keep the component a thin renderer).
 *
 * Today only ADD_NODE_WITH_CUSTOM_VALUE carries extra per-config inputs (a value + a value type,
 * with JSON parse/minify semantics). The component consults this module instead of hardcoding the
 * template name and validation inline. If additional input-bearing templates are introduced, the
 * intended evolution is to make this a registry keyed by template (or to drive it from template
 * metadata vended by the backend) so the component itself need not change — see the design note in
 * design-docs/cycle-level-enable-disable-program-flow.md.
 */

export const INVALID_JSON_MESSAGE = "Custom value is not valid JSON. Fix it or change the value type.";
export const MISSING_CUSTOM_VALUE_MESSAGE =
    "Please enter a custom value and select its type for the ADD_NODE_WITH_CUSTOM_VALUE template.";
export const EMPTY_LIST_MESSAGE =
    "Custom value must contain at least one non-empty list item (commas and whitespace only is not allowed).";

/**
 * Whether the given template requires the custom value + type inputs (and their validation/render).
 */
export function requiresCustomValue(template) {
    return template === ADD_NODE_WITH_CUSTOM_VALUE_TEMPLATE;
}

/**
 * Whether the given value/type pair is valid JSON when the type is JSON. Non-JSON types are always
 * considered valid here (no structural constraint).
 */
export function isValidCustomValue(value, type) {
    if (type !== CUSTOM_VALUE_TYPES.JSON) {
        return true;
    }
    try {
        JSON.parse(value);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Validates the custom value + type for submission. Returns an error message string when invalid, or
 * null when valid. Encapsulates the "required + JSON-parseable" rule so the component does not inline
 * template-specific validation.
 */
export function validateCustomValueForSubmit(value, type) {
    if (!value || !value.trim() || !type) {
        return MISSING_CUSTOM_VALUE_MESSAGE;
    }
    if (!isValidCustomValue(value, type)) {
        return INVALID_JSON_MESSAGE;
    }
    // A LIST of only delimiters/whitespace (e.g. ",, ,") passes the non-blank check above but
    // normalizes to "" (all elements dropped); reject it so an empty list is never submitted.
    if (type === CUSTOM_VALUE_TYPES.LIST && normalizeCustomValue(value, type) === "") {
        return EMPTY_LIST_MESSAGE;
    }
    return null;
}

/**
 * Normalizes the custom value for storage: JSON is parsed and re-serialized (minified) so pasted
 * pretty-printed JSON is stored compact; STRING/LIST are returned as-is. Assumes the value is already
 * known valid for its type (call isValidCustomValue / validateCustomValueForSubmit first).
 */
export function normalizeCustomValue(value, type) {
    if (type === CUSTOM_VALUE_TYPES.JSON && value) {
        return JSON.stringify(JSON.parse(value));
    }
    if (type === CUSTOM_VALUE_TYPES.LIST && value) {
        return value.split(",").map(element => element.trim()).filter(element => element !== "").join(",");
    }
    if (type === CUSTOM_VALUE_TYPES.STRING && value) {
        return value.trim();
    }
    return value;
}

/**
 * Pure computation of what should happen to the entered value when the JSON semantics are applied
 * to it (invoked both when the user selects the JSON type and when they blur the value field while
 * the type is already JSON). It never mutates React state itself — it returns the intended outcome
 * so the component stays a thin renderer (UTR Architecture Tenet 9) and this logic is unit-testable
 * without rendering the Meridian Select (which is not drivable in jsdom).

 */
export function computeJsonMinifyOutcome(value) {
    if (!value) {
        return {};
    }
    if (isValidCustomValue(value, CUSTOM_VALUE_TYPES.JSON)) {
        return {value: normalizeCustomValue(value, CUSTOM_VALUE_TYPES.JSON), clearInvalidAlert: true};
    }
    return {showInvalidAlert: true};
}

/**
 * Pure computation of what should happen when the user selects a value TYPE from the dropdown, given
 * the value already entered. Returned so the component wrapper stays a thin state-mapper and both
 * branches are unit-testable without the Meridian Select (not drivable in jsdom).
 */
export function computeTypeChangeOutcome(value, selectedType) {
    if (selectedType === CUSTOM_VALUE_TYPES.JSON) {
        return computeJsonMinifyOutcome(value);
    }
    return {clearInvalidAlert: true};
}

/**
 * Applies an outcome object ({ value?, clearInvalidAlert?, showInvalidAlert? }) — as produced by
 * computeJsonMinifyOutcome / computeTypeChangeOutcome — onto component state via injected callbacks.
 */
export function applyCustomValueOutcome(outcome, {setValue, clearInvalidJsonAlert, showInvalidJsonAlert}) {
    if (outcome.value !== undefined) {
        setValue(outcome.value);
    }
    if (outcome.clearInvalidAlert) {
        clearInvalidJsonAlert();
    } else if (outcome.showInvalidAlert) {
        showInvalidJsonAlert();
    }
}

/**
 * Full "value type was selected" behavior, orchestrated over injected state callbacks so the whole
 * flow (including the JSON-minify vs clear-stale-alert branch) is unit-testable without the Meridian
 * Select (which cannot be operated under jsdom). The component's onChange handler is a one-line
 * delegate to this. `setType` records the selected type; the rest apply the computed outcome.
 */
export function applyTypeChange(value, selectedType, {setType, setValue, clearInvalidJsonAlert, showInvalidJsonAlert}) {
    setType(selectedType);
    applyCustomValueOutcome(computeTypeChangeOutcome(value, selectedType),
        {setValue, clearInvalidJsonAlert, showInvalidJsonAlert});
}



// WEBPACK FOOTER //
// ./src/components/create-config/customValueTemplate.js